import { api } from './api';

export interface CustomerTransactionSummary {
  totalInvoices: number;
  totalRevenue: number;
  paymentsReceived: number;
  outstanding: number;
}

export interface Transaction {
  id: number;
  date: string;
  customer: string;
  type: string;
  reference: string;
  amount: number;
  status: string;
  jobNumber?: string;
  bankAccount?: string;
}

export interface CustomerTransactionReport {
  summary: CustomerTransactionSummary;
  transactions: Transaction[];
}

export interface JobFinancials {
  totalBills: number;
  totalInvoices: number;
  profit: number;
  profitMargin: number;
}

export interface JobDetails {
  id: number;
  jobNumber: string;
  description: string;
  customer: string;
  startDate: string;
  endDate?: string;
  status: string;
}

export interface JobReport {
  job: JobDetails;
  financials: JobFinancials;
  bills: any[];
  invoices: any[];
}

export interface InvoiceHistoryItem {
  event: string;
  date: string;
  description: string;
  amount?: number;
  status: string;
}

export interface InvoiceHistory {
  invoice: any;
  financials: any;
  history: InvoiceHistoryItem[];
  items: any[];
}

export interface VendorPerformanceMetrics {
  totalVendors: number;
  averagePaymentTime: number;
  onTimePaymentRate: number;
}

export interface VendorPerformanceReport {
  summary: VendorPerformanceMetrics;
  topVendors: any[];
  paymentTermsAnalysis: any[];
}

export interface BankAccountBalance {
  id: number;
  accountName: string;
  accountNumber: string;
  bankName: string;
  balance: number;
  totalInflows: number;
  totalOutflows: number;
}

export interface BankStatement {
  accountBalances: BankAccountBalance[];
  transactions: Transaction[];
  summary: {
    totalAccounts: number;
    totalBalance: number;
    totalInflows: number;
    totalOutflows: number;
  };
}

export interface CashFlowSummary {
  totalInflows: number;
  totalOutflows: number;
  netCashFlow: number;
  totalInvoiced: number;
  totalBilled: number;
  collectionRate: number;
  paymentRate: number;
}

export interface MonthlyData {
  month: string;
  inflows: number;
  outflows: number;
  netFlow: number;
}

export interface CashFlowAnalysis {
  summary: CashFlowSummary;
  monthlyData: MonthlyData[];
  topCustomerPayments: any[];
  topVendorPayments: any[];
}

export interface ProfitLossData {
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  profitMargin: number;
  grossProfit: number;
  operatingExpenses: number;
  administrativeExpenses: number;
  marketingExpenses: number;
}

export interface AdvancedAnalytics {
  profitLoss: ProfitLossData;
  balanceSheet: any;
  kpis: any;
  monthlyTrends: any[];
  topJobs: any[];
}

class ReportsService {
  private readonly endpoint = '/reports';

  constructor() {
    console.log('🔥🔥🔥 REPORTS SERVICE LOADED AT:', new Date().toISOString(), '🔥🔥🔥');
    console.log('🚀 NO MORE MOCK DATA - USING REAL API CALLS!');
    console.log('💥 Cache timestamp:', Date.now());
  }

  async getCustomerTransactionReport(params?: {
    customerId?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  }): Promise<CustomerTransactionReport> {
    const queryParams = new URLSearchParams();
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.status) queryParams.append('status', params.status);

    const response = await api.get(`${this.endpoint}/customer-transactions?${queryParams}`);
    return response.data.data;
  }

  async getJobReport(jobNumber: string): Promise<JobReport> {
    const response = await api.get(`${this.endpoint}/job-report?jobNumber=${encodeURIComponent(jobNumber)}`);
    return response.data;
  }

  async getInvoiceHistory(invoiceNumber: string): Promise<InvoiceHistory> {
    const response = await api.get(`${this.endpoint}/invoice-history?invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
    return response.data;
  }

  async getVendorTransactionReport(params?: {
    vendorId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<CustomerTransactionReport> {
    const queryParams = new URLSearchParams();
    if (params?.vendorId) queryParams.append('vendorId', params.vendorId.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`${this.endpoint}/vendor-transactions?${queryParams}`);
    return response.data;
  }

  async getVendorPerformanceReport(): Promise<VendorPerformanceReport> {
    const response = await api.get(`${this.endpoint}/vendor-performance`);
    return response.data;
  }

  async getBillHistory(billNumber: string): Promise<InvoiceHistory> {
    const response = await api.get(`${this.endpoint}/bill-history?billNumber=${encodeURIComponent(billNumber)}`);
    return response.data;
  }

  async getBankAccountStatement(params?: {
    accountId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<BankStatement> {
    const queryParams = new URLSearchParams();
    if (params?.accountId) queryParams.append('accountId', params.accountId.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`${this.endpoint}/bank-statement?${queryParams}`);
    return response.data?.data ?? response.data;
  }

  async getCashFlowAnalysis(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<CashFlowAnalysis> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`${this.endpoint}/cash-flow?${queryParams}`);
    return response.data?.data ?? response.data;
  }

  async getAdvancedAnalytics(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<AdvancedAnalytics> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(`${this.endpoint}/analytics?${queryParams}`);
    return response.data?.data ?? response.data;
  }
}

export const reportsService = new ReportsService();